import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  Type,
  ViewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { IonicModule, IonInput } from '@ionic/angular';
import { MaskitoDirective } from '@maskito/angular';
import type { MaskitoOptions } from '@maskito/core';
import { TranslateModule } from '@ngx-translate/core';
import { DynamicHostDirective } from 'src/app/common/directives/dynamic-host.directive';
import { shakeTrashCanAnimation } from './garbage-animation';
import { InputMaskService } from './sol-input-mask.component';
import { AUTO_CAPITALIZE, INPUT_TYPE } from './sol-input.enums';

@Component({
  selector: 'sol-input',
  templateUrl: './sol-input.component.html',
  styleUrls: ['./sol-input.component.scss'],
  standalone: true,
  imports: [
    IonicModule,
    CommonModule,
    DynamicHostDirective,
    TranslateModule,
    MaskitoDirective,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SolInputComponent),
      multi: true,
    },
  ],
  animations: [shakeTrashCanAnimation],
})
export class SolInputComponent implements AfterViewInit {
  /** @description Sets if the field can be edited.`default: false`
   */
  @Input() editMode: boolean = false;
  /** @description Determines if an icon should be displayed. `default: false`  */
  @Input() showIcon = false;
  /** @description If the icon is part of IONICONS use the string. `default: undefined */
  @Input() icon: string | undefined;
  /** @description Alertnative icon, use the path to the file. `default: undefined */
  @Input() altIcon: string | undefined;
  /** @description Label for the input. `default: undefined */
  @Input() label: string | undefined;
  /** @description Determines if the input should be 100% width. `default: true` */
  @Input() fullWidth: boolean = true;
  /** @description In case input needs to be a component itself. `default: undefined` */
  @Input() componentType: Type<any> | undefined;
  /** @description Used for helper text. `default: undefined` */
  @Input() helperText: string | undefined;
  /** @description texto para erro */
  @Input() errorText: string | undefined;
  /** @description Placeholder to be displayed as masked value. `default: undefined` */
  @Input() placeHolder: string | undefined;
  /** @description Prefix to be displayed as masked value. `default: undefined` */
  @Input() prefix: string | undefined;
  /** @description If the input field should be auto capitalized base on `AUTO_CAPITALIZE` enum. `default: OFF`. */
  @Input() autoCapitalize: AUTO_CAPITALIZE = AUTO_CAPITALIZE.OFF;
  /** @description {boolean} Determines if the "X" button to clear the input should be displayed. `default: false` */
  @Input() clearInput: boolean = false;
  /** @description {string} Type of input based on `INPUT_TYPE` enum. `default: undefined */
  @Input() inputType: INPUT_TYPE | undefined = undefined;
  @Input() useMask: boolean = false; /// vai ser utilizado para a mascara do WhatsApp
  @Input() inputKey: string | undefined;
  @Input() fill: 'solid' | 'outline' | undefined;
  @Input() secondaryIcon: string | undefined;
  @Input() showTrashIconOnHover: boolean = false;
  @Input() showLine: 'start' | 'full' | 'none' = 'none';
  /** mostra contador de caracteres */
  @Input() showCharCounter: boolean = false;
  /** número máximo de caracteres */
  @Input() maxChars: number | undefined;
  @Input() maskOptions: MaskitoOptions | null = null;
  @Output() valueChange = new EventEmitter<string>();

  @Output() secondaryIconClick = new EventEmitter<void>();
  @Output() deleteEventClick = new EventEmitter<void>();
  @ViewChild(IonInput) ionInput: any;
  @ViewChild(DynamicHostDirective, { static: false }) dynamicHost:
    | DynamicHostDirective
    | undefined;
  INPUT_TYPE = INPUT_TYPE;
  value: string | null = null;
  showGarbageIcon = false;
  garbageIconState = 'normal' as string;
  onChange: any = () => {};
  onTouched: any = () => {};
  private componentRef: any;
  selectedCountryIso: any;

  constructor(
    private InputMaskService: InputMaskService,
    private cd: ChangeDetectorRef
  ) {}

  ngAfterViewInit(): void {
    if ((!this.maskOptions && this.inputKey) || this.useMask) {
      // define a máscara para o maskito com os dados do back-end assim que inicia a aplicação
      if (['telefone', 'whatsapp', 'telegram'].includes(this.inputKey || '')) {
        this.maskOptions = this.InputMaskService.getDynamicPhoneMask(
          this.value || ''
        );
      } else {
        this.maskOptions = this.InputMaskService.getMaskByKey(this.inputKey);
      }
      this.cd.markForCheck();
    }

    /** aplica máscara no valor inicial (ex: vindo do banco) */
    if (this.maskOptions && this.value) {
      this.value = this.InputMaskService.applyMaskOnStart(
        this.value,
        this.maskOptions
      );
      this.cd.markForCheck();
    }

    this.loadComponent();
    this.startAnimationLoop();
    if (this.inputType === this.INPUT_TYPE.PASS) {
      this.ionInput.type = this.INPUT_TYPE.PASS;
      this.cd.markForCheck();
    }
    this.cd.detectChanges();
  }

  loadComponent() {
    if (this.componentType && this.dynamicHost) {
      const viewContainerRef = this.dynamicHost?.viewContainerRef;
      viewContainerRef?.clear();
      this.componentRef = viewContainerRef?.createComponent(this.componentType);
      if (this.value !== '') {
        (this.componentRef?.instance).videoID = this.value;
      }
    }
  }

  writeValue(value: string): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Implement if needed
  }

  deleteClick(eventObj: any) {
    this.deleteEventClick.emit(eventObj);
  }

  onInputChange(event: any): void {
    this.value = event.target.value;

    // define as mascaras na qual o maskito irá atualizar dinamicamente
    if (['telefone', 'whatsapp', 'telegram'].includes(this.inputKey || '')) {
      this.maskOptions = this.InputMaskService.getDynamicPhoneMask(this.value);
    }

    this.onChange(this.value);
    this.onTouched();

    if (this.componentRef) {
      (this.componentRef?.instance).videoID = this.value;
    }

    this.valueChange.emit(this.value ?? '');
  }

  togglePasswordType() {
    this.ionInput.type =
      this.ionInput.type === INPUT_TYPE.TEXT
        ? INPUT_TYPE.PASS
        : INPUT_TYPE.TEXT;
  }

  emitSecondaryIconClick(event: any): void {
    this.secondaryIconClick.emit(event);
  }

  startAnimationLoop() {
    // Immediately trigger the pop effect without waiting
    if (this.showGarbageIcon) {
      this.triggerAnimation();
    }

    // Then, keep looping every 1.8s
    setInterval(() => {
      if (this.showGarbageIcon) {
        this.triggerAnimation();
      }
    }, 1800 + 600); // 1.8s pause + 600ms animation duration
  }

  triggerAnimation() {
    this.garbageIconState = 'sizeUp';

    setTimeout(() => {
      this.garbageIconState = 'sizeNormal';
      this.startShaking();
    }, 100);
  }

  startShaking() {
    this.garbageIconState = 'shakeLeft';
    setTimeout(() => (this.garbageIconState = 'shakeRight'), 100);
    setTimeout(() => (this.garbageIconState = 'shakeLeft'), 200);
    setTimeout(() => (this.garbageIconState = 'shakeRight'), 300);
    setTimeout(() => (this.garbageIconState = 'shakeLeft'), 400);
    setTimeout(() => (this.garbageIconState = 'shakeRight'), 500);
    setTimeout(() => (this.garbageIconState = 'sizeNormal'), 600);
  }
}
